import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Fade,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useSnackbar } from "notistack";
import backend from "../../../../../../../api";

const PRIMARY = "#f26a63";

const theme = createTheme({
  palette: { primary: { main: PRIMARY } },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: PRIMARY,
          color: "#fff",
          boxShadow: "0 4px 12px rgba(242,106,99,0.25)",
          "&:hover": { backgroundColor: "#e45a53", boxShadow: "0 6px 16px rgba(242,106,99,0.3)" },
          transition: "all 0.2s ease",
        },
        outlinedPrimary: {
          borderColor: PRIMARY,
          color: PRIMARY,
          "&:hover": { backgroundColor: "rgba(242,106,99,0.08)" },
          transition: "all 0.2s ease",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY },
          transition: "border-color 0.2s ease",
        },
      },
    },
    MuiSelect: { styleOverrides: { icon: { color: PRIMARY } } },
  },
});

const sxModal = (t) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "clamp(320px, 90vw, 480px)",
  bgcolor: "background.paper",
  borderColor: 'white',
  borderRadius: 5,
  overflow: "hidden",
  padding: '10px',
  [t.breakpoints.down("sm")]: {
    width: "calc(100vw - 12px)",
    borderRadius: 8,
  },
  '&:focus,&:focus-visible': { outline: 'none' }
});

const sxHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  p: 1.5,
};

const sxBody = {
  p: 1.5,
  display: "grid",
  gap: 1.5,
  maxHeight: "65vh",
  overflowY: "auto",
  outline: 'none !important',
};

const sxActions = { p: 1.5, pt: 1 };

const labelText = { fontSize: 12, color: "text.secondary", mb: 0.25 };

const toIsoLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const addMinutes = (hhmm, minutes) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  const total = h * 60 + m + Number(minutes || 0);
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
};

const formatTime12 = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  const p = h >= 12 ? "PM" : "AM";
  const hh = (h % 12) || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${p}`;
};

const formatDatePretty = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function RescheduleModal({ open, onClose, booking, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();

  const availability = booking?.mentorAvailability || booking?.mentorId?.mentorProfile?.availability || {};
  const availableSlots = availability?.availableSlots || {};
  const timeSettings = availability?.timeSettings || {};
  const durations = Array.isArray(timeSettings?.durations) ? timeSettings.durations : [];

  const lockedDuration = Number(booking?.duration) || Number(durations?.[0]?.time) || 30;
  const durKey = String(lockedDuration);

  const todayStr = toIsoLocalDate(new Date());
  const validDateKeys = useMemo(() => {
    return Object.keys(availableSlots)
      .filter((d) => d >= todayStr && availableSlots?.[d]?.[durKey])
      .filter((d) => Object.values(availableSlots[d][durKey] || {}).some((v) => v === "available"))
      .sort((a, b) => a.localeCompare(b));
  }, [availableSlots, durKey, todayStr]);

  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [selectedStart, setSelectedStart] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const currentIso = booking?.date || "";
    const preferred = validDateKeys.includes(currentIso) ? currentIso : validDateKeys[0] || "";
    setSelectedDateKey(preferred);
    setSelectedStart("");
  }, [open, booking?.date, validDateKeys]);

  const slotOptions = useMemo(() => {
    if (!selectedDateKey) return [];
    const map = availableSlots?.[selectedDateKey]?.[durKey] || {};
    return Object.keys(map)
      .filter((start) => map[start] === "available")
      .sort((a, b) => a.localeCompare(b))
      .map((start) => ({
        start,
        end: addMinutes(start, lockedDuration),
        label: `${formatTime12(start)} – ${formatTime12(addMinutes(start, lockedDuration))}`,
      }));
  }, [availableSlots, selectedDateKey, durKey, lockedDuration]);

  const handleConfirm = async () => {
    if (!selectedDateKey) {
      enqueueSnackbar("Please select a date.", { variant: "error" });
      return;
    }
    if (!selectedStart) {
      enqueueSnackbar("Please select a time.", { variant: "error" });
      return;
    }
    const end = addMinutes(selectedStart, lockedDuration);

    try {
      setSaving(true);
      await backend.put(`/bookings/${booking._id}`, {
        date: selectedDateKey,
        start: selectedStart,
        end,
      });
      enqueueSnackbar("Booking rescheduled successfully!", { variant: "success" });
      onClose?.();
      onSuccess?.();
    } catch (err) {
      console.error("Reschedule error:", err);
      enqueueSnackbar(err?.response?.data?.error || "Failed to reschedule booking.", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const DurationPill = () => {
    const match = durations.find((d) => Number(d?.time) === lockedDuration);
    const priceTxt = match?.price ? ` — $${match.price}` : "";
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 16,
          bgcolor: "rgba(242,106,99,0.1)",
          border: "1px solid rgba(242,106,99,0.28)",
          color: PRIMARY,
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {lockedDuration} min{priceTxt} (locked)
      </Box>
    );
  };

  const noDates = validDateKeys.length === 0;

  return (
    <ThemeProvider theme={theme}>
      <Modal open={open} onClose={onClose} closeAfterTransition >
        <Fade in={open} timeout={200}>
          <Box sx={sxModal(theme)}>
            <Box sx={sxHeader}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Reschedule Booking
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                  Select a new date and time. Duration is fixed.
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box sx={sxBody}>
              <Box>
                <Typography sx={labelText}>Duration</Typography>
                <DurationPill />
              </Box>

              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="date-select-label">Date</InputLabel>
                  <Select
                    labelId="date-select-label"
                    label="Date"
                    value={selectedDateKey}
                    onChange={(e) => {
                      setSelectedDateKey(e.target.value);
                      setSelectedStart("");
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    {validDateKeys.map((d) => (
                      <MenuItem key={d} value={d}>
                        {formatDatePretty(d)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {noDates && (
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
                    No available dates for this session duration.
                  </Typography>
                )}
              </Box>

              <Box>
                <FormControl fullWidth size="small" disabled={!selectedDateKey || slotOptions.length === 0}>
                  <InputLabel id="time-select-label">Time</InputLabel>
                  <Select
                    labelId="time-select-label"
                    label="Time"
                    value={selectedStart}
                    onChange={(e) => setSelectedStart(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    {slotOptions.map((s) => (
                      <MenuItem key={s.start} value={s.start}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedDateKey && slotOptions.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
                    No available slots for this date.
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            <Box sx={sxActions}>
              <Stack direction="row" spacing={1}>
                <Button onClick={onClose} variant="outlined" fullWidth style={{ borderRadius: '10px' }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={!selectedDateKey || !selectedStart || saving}
                  variant="contained"
                  fullWidth
                  style={{ borderRadius: '10px' }}
                >
                  {saving ? "Saving…" : "Confirm"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </ThemeProvider>
  );
}